import React from 'react';
import { DateTime } from 'luxon';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { getDesktopVersion, isWithinAnotherApp } from 'containerApp';

interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
}

const ItemRow = ({ labelId, children }: { labelId: string; children: any }) => {
  return (
    <Grid container item alignContent="space-between" xs={12}>
      <Grid item xs={6}>
        <Typography color="textSecondary" variant="body1">
          <FormattedMessage id={labelId} />
        </Typography>
      </Grid>
      <Grid item xs={6}>
        {children}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    maxWidth: '460px'
  },
  content: {
    paddingBottom: theme.spacing(4)
  },
  actions: {
    paddingBottom: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily
  },
  itemValue: {
    marginTop: theme.spacing(0.2),
    fontFamily: theme.typography.body1.fontFamily
  }
}));

export const AboutDialog = ({
  open,
  onClose
}: AboutDialogProps): JSX.Element => {
  const classes = useStyles();
  const copyrightYear = DateTime.local().year;

  const version = isWithinAnotherApp() ? getDesktopVersion() : window.version;

  return (
    <Dialog
      data-testid="about-dialog"
      open={open}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle className={classes.title}>
        <FormattedMessage id="aboutDialog.about" />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid
          container
          alignContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <ItemRow labelId="aboutDialog.applicationName">
            <Typography className={classes.itemValue}>
              <FormattedMessage id="aboutDialog.timeIntelligence" />
            </Typography>
          </ItemRow>
          <ItemRow labelId="aboutDialog.version">
            <Typography
              data-testid="application-version"
              className={classes.itemValue}
            >
              {version}
            </Typography>
          </ItemRow>
          <ItemRow labelId="aboutDialog.licenseInformation">
            <Grid item xs={12}>
              <Link
                href="https://www.replicon.com/terms-and-conditions/privacy-policy/"
                target="_blank"
              >
                <Typography className={classes.itemValue}>
                  <FormattedMessage id="aboutDialog.privacyPolicy" />
                </Typography>
              </Link>
            </Grid>
            <Grid item xs={12}>
              <Link
                href="https://www.replicon.com/terms-and-conditions/"
                target="_blank"
              >
                <Typography className={classes.itemValue}>
                  <FormattedMessage id="aboutDialog.termsAndConditions" />
                </Typography>
              </Link>
            </Grid>
          </ItemRow>
          <ItemRow labelId="aboutDialog.copyrightInformation">
            <Typography className={classes.itemValue}>
              <FormattedMessage
                id="aboutDialog.copyright"
                values={{ copyrightYear }}
              />
            </Typography>
          </ItemRow>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button color="primary" variant="contained" onClick={onClose}>
          <FormattedMessage id="aboutDialog.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
